/**
 * Cache Event Listener - WebSocket Client
 * 
 * Connects to the server's cache invalidation WebSocket to receive
 * real-time cache invalidation events and synchronize across devices.
 * 
 * Features:
 * - Auto-connects with JWT authentication
 * - Subscribes to user-specific cache events
 * - Triggers React Query cache invalidation
 * - Auto-reconnects on disconnect
 * - Cross-device synchronization (<500ms)
 * 
 * Usage:
 * ```ts
 * const listener = new CacheEventListener(queryClient);
 * listener.connect();
 * ```
 */

import { io, Socket } from 'socket.io-client';
import type { QueryClient } from '@tanstack/react-query';

// Cache event types from server (server/utils/cacheInvalidation.js)
export type CacheEventType = 
  | 'cache:invalidate:profile'
  | 'cache:invalidate:card'
  | 'cache:invalidate:qr'
  | 'cache:invalidate:appearance'
  | 'cache:invalidate:wallet'
  | 'cache:invalidate:qr-share'
  | 'cache:invalidate:ai-notes'
  | 'cache:invalidate:forms'
  | 'cache:invalidate:analytics'
  | 'cache:invalidate:team'
  | 'cache:invalidate:corporate'
  | 'cache:invalidate:global';

export interface CacheInvalidationEvent {
  type: CacheEventType;
  userId?: string;
  resourceId?: string;
  timestamp: number;
  scope?: 'user' | 'global';
}

export class CacheEventListener {
  private socket: Socket | null = null;
  private queryClient: QueryClient;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private readonly serverUrl: string;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
    this.serverUrl = import.meta.env.VITE_API_URL || 'http://localhost:3030';
  }

  /**
   * Connect to the cache WebSocket server
   */
  public connect(): void {
    if (this.socket?.connected) {
      console.log('📡 Cache listener already connected');
      return;
    }

    const token = this.getAuthToken();
    if (!token) {
      console.warn('⚠️ No auth token found - cache sync disabled');
      return;
    }

    console.log('🔌 Connecting cache event listener to:', this.serverUrl);

    this.socket = io(this.serverUrl, {
      auth: (cb: (data: { token: string }) => void) => {
        cb({ token: this.getAuthToken() || '' });
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    this.setupEventHandlers();
  }

  /**
   * Disconnect from the cache WebSocket server
   */
  public disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting cache event listener');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Check if the listener is connected
   */
  public connected(): boolean {
    return this.isConnected;
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log('✅ Cache listener connected:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('🔌 Cache listener disconnected:', reason);
    });

    this.socket.on('connect_error', () => {
      this.reconnectAttempts++;
    });

    // Cache invalidation events
    this.socket.on('cache:invalidate:profile', (data: CacheInvalidationEvent) => {
      console.log('🔄 Profile cache invalidated:', data);
      this.invalidateQueries(['profile', 'useraccountbio']);
      // Clear client-side sessionStorage cache so useUserProfile re-fetches fresh data
      sessionStorage.removeItem('user_profile');
      sessionStorage.removeItem('profile_cache_version');
      // Dispatch a DOM event so useUserProfile hook can trigger a re-fetch
      window.dispatchEvent(new CustomEvent('profile-cache-cleared', { detail: data }));
    });

    this.socket.on('cache:invalidate:card', (data: CacheInvalidationEvent) => {
      console.log('🔄 Card cache invalidated:', data);
      this.invalidateQueries(['card', 'cards', 'card-view']);
    });

    this.socket.on('cache:invalidate:qr', (data: CacheInvalidationEvent) => {
      console.log('🔄 QR cache invalidated:', data);
      this.invalidateQueries(['qr-designer', 'qr-list', 'qr-stats']);
      window.dispatchEvent(new CustomEvent('cache:invalidate:qr', { detail: data }));
    });

    this.socket.on('cache:invalidate:appearance', (data: CacheInvalidationEvent) => {
      console.log('🔄 Appearance cache invalidated:', data);
      this.invalidateQueries(['branding', 'appearance', 'card-branding']);
    });

    this.socket.on('cache:invalidate:wallet', (data: CacheInvalidationEvent) => {
      console.log('🔄 Wallet cache invalidated:', data);
      this.invalidateQueries(['wallet', 'wallet-cards']);
    });

    this.socket.on('cache:invalidate:qr-share', (data: CacheInvalidationEvent) => {
      console.log('🔄 QR Share cache invalidated:', data);
      this.invalidateQueries(['qr-share', 'subscribers', 'broadcasts', 'qr-analytics']);
      window.dispatchEvent(new CustomEvent('qr-share-cache-cleared', { detail: data }));
    });

    this.socket.on('cache:invalidate:ai-notes', (data: CacheInvalidationEvent) => {
      console.log('🔄 AI Notes cache invalidated:', data);
      this.invalidateQueries(['ai-notes', 'conversations', 'saved-notes']);
    });

    this.socket.on('cache:invalidate:forms', (data: CacheInvalidationEvent) => {
      console.log('🔄 Forms cache invalidated:', data);
      this.invalidateQueries(['forms', 'form-config', 'form-submissions']);
    });

    this.socket.on('cache:invalidate:analytics', (data: CacheInvalidationEvent) => {
      console.log('🔄 Analytics cache invalidated:', data);
      this.invalidateQueries(['analytics', 'stats', 'tracking']);
    });

    this.socket.on('cache:invalidate:team', (data: CacheInvalidationEvent) => {
      console.log('🔄 Team cache invalidated:', data);
      this.invalidateQueries(['teams', 'team', 'team-members', 'team-activity']);
    });

    this.socket.on('cache:invalidate:corporate', (data: CacheInvalidationEvent) => {
      console.log('🔄 Corporate cache invalidated:', data);
      window.dispatchEvent(new CustomEvent('cache:invalidate:corporate', { detail: data }));
    });

    this.socket.on('cache:invalidate:global', (data: CacheInvalidationEvent) => {
      console.log('🔄 Global cache invalidated:', data);
      // Invalidate all queries for global changes
      this.queryClient.invalidateQueries();
    });

    // Health check
    this.socket.on('cache:health', (data: any) => {
      console.log('💚 Cache health check:', data);
    });

    // Force logout — emitted when admin deactivates or deletes this account
    this.socket.on('force:logout', (data: { reason?: string }) => {
      console.warn('🔒 Force logout received:', data.reason);
      // Clear all auth and profile state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('auth_version');
      sessionStorage.removeItem('user_profile');
      sessionStorage.removeItem('profile_cache_version');
      // Disconnect socket cleanly before redirect
      this.disconnect();
      // Redirect to login with a reason flag so the login page can show a message
      window.location.href = `/login?reason=${data.reason || 'deactivated'}`;
    });
  }

  /**
   * Invalidate React Query caches for specific query keys
   */
  private invalidateQueries(queryKeys: string[]): void {
    queryKeys.forEach(key => {
      this.queryClient.invalidateQueries({
        queryKey: [key],
        refetchType: 'active', // Only refetch active queries
      });
    });
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }
}

/**
 * Singleton instance - created once in App.tsx
 */
let cacheListenerInstance: CacheEventListener | null = null;

export function initializeCacheListener(queryClient: QueryClient): CacheEventListener {
  if (!cacheListenerInstance) {
    cacheListenerInstance = new CacheEventListener(queryClient);
    cacheListenerInstance.connect();
  }
  return cacheListenerInstance;
}

export function getCacheListener(): CacheEventListener | null {
  return cacheListenerInstance;
}

export function disconnectCacheListener(): void {
  if (cacheListenerInstance) {
    cacheListenerInstance.disconnect();
    cacheListenerInstance = null;
  }
}
